import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import type { ResetPassword } from "../../../../common/src";
import { resetPasswordSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { navigate } from "../../hooks/useRoute";
import { DEFAULT_ROUTE } from "../../routes";
import { useSession } from "../../session/SessionContext";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { Button } from "../shared/Button";
import { Notice } from "../shared/Notice";
import { useFormStyles } from "../shared/styles";
import { AuthLayout } from "./AuthLayout";
import { NewPasswordFields } from "./NewPasswordFields";

/** Opened from the reset email: choose a new password, then continue signed in. */
export const ResetPasswordPage = ({ token }: { token: string }) => {
  const form = useFormStyles();
  const { signedIn } = useSession();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const reset = useAction(
    useCallback(
      (input: ResetPassword) => trpc.auth.resetPassword.mutate(input),
      []
    )
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = resetPasswordSchema.safeParse({ token, password });
    const fieldErrors = parsed.success ? {} : toFieldErrors(parsed.error);
    if (password && confirm !== password) {
      fieldErrors.confirm = "Passwords don't match.";
    }
    if (!parsed.success || Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    const user = await reset.run(parsed.data);
    if (user) {
      navigate(DEFAULT_ROUTE);
      signedIn(user);
    }
  };

  return (
    <AuthLayout width={420}>
      <h2>Choose a new password</h2>
      <p>This signs you out on your other devices.</p>
      {(reset.error ?? errors.token) && (
        <Notice kind="error">
          {reset.error ??
            "This reset link is incomplete. Open the link from your email again."}
        </Notice>
      )}
      {reset.error && (
        <p>
          <a href="#/forgot-password">Request a new reset link</a>
        </p>
      )}
      <form onSubmit={(e) => void submit(e)} noValidate className={form.stack}>
        <NewPasswordFields
          password={password}
          confirm={confirm}
          onPasswordChange={setPassword}
          onConfirmChange={setConfirm}
          passwordError={errors.password}
          confirmError={errors.confirm}
        />
        <div className={form.actions}>
          <Button type="submit" disabled={reset.pending}>
            {reset.pending ? "Saving…" : "Save password and sign in"}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};
